import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Box,
  Typography,
  Drawer,
  IconButton,
  Chip,
  Divider,
  Stack,
  Paper,
  alpha,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  CircularProgress,
  Tooltip,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import Close from '@mui/icons-material/Close'
import Person from '@mui/icons-material/Person'
import Visibility from '@mui/icons-material/Visibility'
import Schedule from '@mui/icons-material/Schedule'
import ContentCopy from '@mui/icons-material/ContentCopy'
import { toast } from 'react-toastify'
import { getImageLink } from 'src/mentor/helpers/utilityFunctions'

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: 520,
    maxWidth: '95vw',
    boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
  },
}))

const DrawerHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: '#fff',
}))

const MentorCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1.5),
  border: '1px solid',
  borderColor: theme.palette.grey[200],
  marginBottom: theme.spacing(1.5),
}))

const AuditorItem = styled(Paper)(({ theme, expired }) => ({
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(1),
  backgroundColor: expired ? alpha('#ef4444', 0.05) : alpha('#10b981', 0.05),
  border: `1px solid ${expired ? alpha('#ef4444', 0.2) : alpha('#10b981', 0.2)}`,
  marginBottom: theme.spacing(1),
}))

const InfoRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(1),
}))

const formatDateTime = (dateTimeStr) => {
  if (!dateTimeStr) return 'N/A'
  try {
    const date = new Date(dateTimeStr)
    if (Number.isNaN(date.getTime())) return 'N/A'
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  } catch {
    return dateTimeStr
  }
}

const isExpired = (expiresOn) => {
  if (!expiresOn) return false
  try {
    return new Date(expiresOn) < new Date()
  } catch {
    return false
  }
}

const formatExpiration = (expiresOn) => {
  if (!expiresOn) return 'No expiration'
  const expired = isExpired(expiresOn)
  const formatted = formatDateTime(expiresOn)
  return expired ? `Expired: ${formatted}` : `Expires: ${formatted}`
}

const getInitials = (name) => {
  if (!name) return '?'
  const words = name.trim().split(/\s+/).filter((w) => w.length > 0)
  if (words.length > 1) {
    return (words[0][0] + words[words.length - 1][0]).toUpperCase()
  } else if (words.length === 1 && words[0].length > 0) {
    return words[0].substring(0, 2).toUpperCase()
  }
  return '?'
}

export default function MentorsAndAuditorsDrawer({
  open,
  onClose,
  chamberRoom,
  clinicLocationTitle,
  allMentors = [],
}) {
  if (!chamberRoom) return null

  const mentorsAndAuditors = chamberRoom?.mentorsAndAuditors || []
  
  // Debug: Log to help troubleshoot
  if (process.env.NODE_ENV === 'development') {
    console.log('MentorsAndAuditorsDrawer - allMentors count:', allMentors.length)
    console.log('MentorsAndAuditorsDrawer - mentorsAndAuditors:', mentorsAndAuditors)
  }

  // Helper function to find mentor by ID (checking id, uid, and userId fields)
  const findMentorById = useCallback((mentorId) => {
    if (!mentorId || !allMentors || allMentors.length === 0) return null
    
    // Try exact matches first
    let mentor = allMentors.find((m) => {
      return (
        m?.id === mentorId ||
        m?.uid === mentorId ||
        m?.userId === mentorId
      )
    })
    
    // If not found, try string comparisons
    if (!mentor) {
      mentor = allMentors.find((m) => {
        const mId = String(m?.id || '').trim()
        const mUid = String(m?.uid || '').trim()
        const mUserId = String(m?.userId || '').trim()
        const searchId = String(mentorId || '').trim()
        
        return (
          (mId && mId === searchId) ||
          (mUid && mUid === searchId) ||
          (mUserId && mUserId === searchId)
        )
      })
    }
    
    return mentor || null
  }, [allMentors])

  // Create a map of mentor IDs to full mentor profiles for quick lookup
  const mentorInfoMap = useMemo(() => {
    const map = new Map()
    mentorsAndAuditors.forEach((entry) => {
      if (entry?.mentorId) {
        const mentor = findMentorById(entry.mentorId)
        if (mentor) {
          map.set(entry.mentorId, mentor)
          // Also store by other possible ID formats for lookup
          if (mentor.id) map.set(mentor.id, mentor)
          if (mentor.uid) map.set(mentor.uid, mentor)
          if (mentor.userId) map.set(mentor.userId, mentor)
        }
      }
    })
    return map
  }, [mentorsAndAuditors, findMentorById])

  // Get auditor info from allMentors
  const getAuditorInfo = (auditorId) => {
    const mentor = findMentorById(auditorId)
    if (mentor) {
      return {
        id: mentor.id,
        name: mentor.name || mentor.firstName || null,
        profilePicId: mentor.profilePicId || mentor.profilePictureId || null,
      }
    }
    return { id: auditorId, name: null, profilePicId: null }
  }

  // Copy ID to clipboard
  const handleCopyId = useCallback((id, label = 'ID') => {
    if (!id) return
    navigator.clipboard.writeText(id).then(
      () => {
        toast.success(`${label} copied to clipboard`)
      },
      () => {
        toast.error(`Failed to copy ${label}`)
      },
    )
  }, [])

  return (
    <StyledDrawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <DrawerHeader>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h5" fontWeight={600}>
              Mentors & Auditors
            </Typography>
            <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
              <Close />
            </IconButton>
          </Box>
          <InfoRow sx={{ mb: 0, opacity: 0.9 }}>
            <Person sx={{ fontSize: 18 }} />
            <Typography variant="body2">{chamberRoom?.title || 'Chamber Room'}</Typography>
          </InfoRow>
          {clinicLocationTitle && (
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              {clinicLocationTitle}
            </Typography>
          )}
        </DrawerHeader>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          {mentorsAndAuditors.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Person sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.4, mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Mentors Found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No mentors or practitioners have access to this chamber room.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              {mentorsAndAuditors.map((entry, idx) => {
                const mentorId = entry.mentorId
                const checkinMethods = entry.checkinMethods || []
                const auditorIds = entry.auditorIds || []
                const auditAccessList = entry.auditAccessList || []
                const isEnabled = entry.enabled !== false

                // Get mentor info from resolved mentor profile
                const mentorInfo = mentorInfoMap.get(mentorId)
                // If not found in map, try to find it again (in case map wasn't populated correctly)
                const resolvedMentor = mentorInfo || findMentorById(mentorId)
                const mentorName = resolvedMentor?.name || resolvedMentor?.firstName || mentorId
                const mentorProfilePicId = resolvedMentor?.profilePicId || resolvedMentor?.profilePictureId
                
                // Debug log if mentor not found
                if (process.env.NODE_ENV === 'development' && !resolvedMentor) {
                  console.log('Mentor not found for ID:', mentorId, 'Available mentors:', allMentors.map(m => ({ id: m.id, uid: m.uid, userId: m.userId })))
                }

                return (
                  <MentorCard key={idx} elevation={0}>
                    {/* Mentor Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar
                        src={mentorProfilePicId ? getImageLink(mentorProfilePicId) : null}
                        sx={{
                          width: 56,
                          height: 56,
                          bgcolor: alpha('#667eea', 0.1),
                          border: '2px solid',
                          borderColor: alpha('#667eea', 0.3),
                        }}
                      >
                        {getInitials(mentorName)}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ flex: 1 }}>
                            {mentorName}
                          </Typography>
                          <Tooltip title="Copy Mentor ID">
                            <IconButton
                              size="small"
                              onClick={() => handleCopyId(mentorId, 'Mentor ID')}
                              sx={{ 
                                color: 'text.secondary',
                                '&:hover': { color: 'primary.main' }
                              }}
                            >
                              <ContentCopy sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                      <Chip
                        size="small"
                        label={isEnabled ? 'Enabled' : 'Disabled'}
                        color={isEnabled ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </Box>

                    {/* Check-in Methods */}
                    {checkinMethods.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
                          Check-in Methods:
                        </Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {checkinMethods.map((method, mIdx) => (
                            <Chip
                              key={mIdx}
                              size="small"
                              label={method === 'check-in-method-in-person' ? 'In-Person' : 'Remote'}
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    )}

                    <Divider sx={{ my: 1.5 }} />

                    {/* Auditors Section */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <Visibility color="secondary" sx={{ fontSize: 20 }} />
                        <Typography variant="subtitle2" fontWeight={600}>
                          Auditors
                        </Typography>
                        <Chip
                          size="small"
                          label={`${auditorIds.length + auditAccessList.length} total`}
                          color="secondary"
                          variant="outlined"
                          sx={{ ml: 1, height: 20 }}
                        />
                      </Box>

                      {/* Static Auditors */}
                      {auditorIds.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 1 }}>
                            Static Auditors ({auditorIds.length}):
                          </Typography>
                          <Stack spacing={1}>
                            {auditorIds.map((auditorId) => {
                              const auditorInfo = getAuditorInfo(auditorId)
                              const auditorName = auditorInfo.name || auditorId
                              const auditorProfilePicId = auditorInfo.profilePicId

                              return (
                                <Box
                                  key={auditorId}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    p: 1,
                                    borderRadius: 1,
                                    backgroundColor: alpha('#667eea', 0.05),
                                    border: '1px solid',
                                    borderColor: alpha('#667eea', 0.2),
                                  }}
                                >
                                  <Avatar
                                    src={auditorProfilePicId ? getImageLink(auditorProfilePicId) : null}
                                    sx={{
                                      width: 40,
                                      height: 40,
                                      bgcolor: alpha('#667eea', 0.1),
                                      border: '1px solid',
                                      borderColor: alpha('#667eea', 0.3),
                                    }}
                                  >
                                    {getInitials(auditorName)}
                                  </Avatar>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Typography variant="body2" fontWeight={500} noWrap sx={{ flex: 1 }}>
                                        {auditorName}
                                      </Typography>
                                      <Tooltip title="Copy Auditor ID">
                                        <IconButton
                                          size="small"
                                          onClick={() => handleCopyId(auditorId, 'Auditor ID')}
                                          sx={{ 
                                            color: 'text.secondary',
                                            '&:hover': { color: 'primary.main' },
                                            padding: '4px'
                                          }}
                                        >
                                          <ContentCopy sx={{ fontSize: 14 }} />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  </Box>
                                  <Chip
                                    size="small"
                                    label="Permanent"
                                    color="info"
                                    variant="outlined"
                                    sx={{ fontSize: '0.65rem', height: 20 }}
                                  />
                                </Box>
                              )
                            })}
                          </Stack>
                        </Box>
                      )}

                      {/* Time-bound Auditors */}
                      {auditAccessList.length > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 1 }}>
                            Time-bound Auditor Access ({auditAccessList.length}):
                          </Typography>
                          <Stack spacing={1}>
                            {auditAccessList.map((access, aIdx) => {
                              const expired = isExpired(access.expiresOn)
                              const auditorInfo = getAuditorInfo(access.auditorMentorId)
                              const auditorName = auditorInfo.name || access.auditorMentorId
                              const auditorProfilePicId = auditorInfo.profilePicId

                              return (
                                <AuditorItem key={aIdx} expired={expired} elevation={0}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Avatar
                                      src={auditorProfilePicId ? getImageLink(auditorProfilePicId) : null}
                                      sx={{
                                        width: 40,
                                        height: 40,
                                        bgcolor: expired
                                          ? alpha('#ef4444', 0.1)
                                          : alpha('#10b981', 0.1),
                                        border: '2px solid',
                                        borderColor: expired
                                          ? alpha('#ef4444', 0.3)
                                          : alpha('#10b981', 0.3),
                                      }}
                                    >
                                      {getInitials(auditorName)}
                                    </Avatar>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                        <Typography variant="body2" fontWeight={500} noWrap sx={{ flex: 1 }}>
                                          {auditorName}
                                        </Typography>
                                        <Tooltip title="Copy Auditor ID">
                                          <IconButton
                                            size="small"
                                            onClick={() => handleCopyId(access.auditorMentorId, 'Auditor ID')}
                                            sx={{ 
                                              color: 'text.secondary',
                                              '&:hover': { color: 'primary.main' },
                                              padding: '4px'
                                            }}
                                          >
                                            <ContentCopy sx={{ fontSize: 14 }} />
                                          </IconButton>
                                        </Tooltip>
                                        <Chip
                                          size="small"
                                          label={expired ? 'Expired' : 'Active'}
                                          color={expired ? 'error' : 'success'}
                                          sx={{ height: 18, fontSize: '0.65rem' }}
                                        />
                                      </Box>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Schedule
                                          sx={{
                                            fontSize: 12,
                                            color: expired ? 'error.main' : 'success.main',
                                          }}
                                        />
                                        <Typography variant="caption" color="text.secondary">
                                          {formatExpiration(access.expiresOn)}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Box>
                                </AuditorItem>
                              )
                            })}
                          </Stack>
                        </Box>
                      )}

                      {auditorIds.length === 0 && auditAccessList.length === 0 && (
                        <Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ py: 1 }}>
                          No auditors assigned to this mentor
                        </Typography>
                      )}
                    </Box>
                  </MentorCard>
                )
              })}
            </Stack>
          )}
        </Box>
      </Box>
    </StyledDrawer>
  )
}
